import React, { useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Button } from "primereact/button";
import { useDispatch } from "react-redux";
import "./reset.scss";
import classNames from "classnames";
import * as Yup from "yup";
import { useFormik } from "formik";
import { handlePostRequest } from "../services/PostTemplate";
import { ToastContainer, toast } from "react-toastify";

function ResetPassword() {
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();
    const token = new URLSearchParams(location.search).get("token");

    const validationSchema = Yup.object().shape({
        password: Yup.string().min(6, "Password must be at least 6 characters.").required("This field is required."),
        confirmPassword: Yup.string()
            .oneOf([Yup.ref("password")], "Passwords must match.")
            .required("Please confirm your password."),
    });

    const formik = useFormik({
        validationSchema,
        initialValues: {
            password: "",
            confirmPassword: "",
        },
        onSubmit: async (data) => {
            if (!token) {
                toast.error("This reset link is missing its token. Please request a new one.");
                return;
            }
            setLoading(true);
            try {
                const res = await dispatch(handlePostRequest({ token, password: data.password }, "/resetPasswordWithToken", true, true));
                if (res !== "error" && res?.success) {
                    setDone(true);
                }
            } finally {
                setLoading(false);
            }
        },
    });

    const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);
    const getFormErrorMessage = (name) => isFormFieldValid(name) && <small className="p-error">{formik.errors[name]}</small>;

    return (
        <>
            <ToastContainer />
            <div className="reset_body">
                <div className="reset_container">
                    <div className="logo_section">
                        <img src="https://res.cloudinary.com/dwxqg9so3/image/upload/v1758210476/4wd_logo_big_croped_djegsi.jpg" alt="Move4wd Logo" className="reset_logo" />
                    </div>

                    <div className="reset_content">
                        {!token ? (
                            <>
                                <h1 className="reset_header">Invalid Reset Link</h1>
                                <p className="reset_sub_header">This password reset link is missing its token. Please request a new one.</p>
                                <div className="button_wrapper">
                                    <Button className="reset_button" label="Request New Link" onClick={() => history.push("/reset")} />
                                </div>
                            </>
                        ) : done ? (
                            <>
                                <h1 className="reset_header">Password Updated</h1>
                                <p className="reset_sub_header">Your password has been reset successfully. You can now log in with your new password.</p>
                                <div className="button_wrapper">
                                    <Button className="reset_button" label="Back to Sign In" onClick={() => history.push("/login")} />
                                </div>
                            </>
                        ) : (
                            <>
                                <h1 className="reset_header">Set a New Password</h1>
                                <p className="reset_sub_header">Choose a new password for your account.</p>

                                <form className="reset_form" onSubmit={formik.handleSubmit}>
                                    <div className="input_wrapper">
                                        <input
                                            id="password"
                                            className={classNames({ "p-invalid": isFormFieldValid("password") }, "reset_input")}
                                            name="password"
                                            value={formik.values.password}
                                            placeholder="New password"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            type="password"
                                        />
                                        {getFormErrorMessage("password")}
                                    </div>

                                    <div className="input_wrapper">
                                        <input
                                            id="confirmPassword"
                                            className={classNames({ "p-invalid": isFormFieldValid("confirmPassword") }, "reset_input")}
                                            name="confirmPassword"
                                            value={formik.values.confirmPassword}
                                            placeholder="Confirm new password"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            type="password"
                                        />
                                        {getFormErrorMessage("confirmPassword")}
                                    </div>

                                    <div className="button_wrapper">
                                        <Button className="reset_button" label="Reset Password" icon={loading ? "pi pi-spin pi-spinner" : ""} iconPos="right" disabled={loading} />
                                    </div>

                                    <div className="back_to_login">
                                        <p>
                                            Remember your password?
                                            <button type="button" className="back_link" onClick={() => history.push("/login")}>
                                                Back to Sign In
                                            </button>
                                        </p>
                                    </div>
                                </form>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

export default ResetPassword;
